import { Box, Divider, Link } from '@chakra-ui/react';

export const RobynVsGoogleMeridianVsMerkatusContent = () => (
  <Box>
    {/* Introduction */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="20px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        fontFamily="marketingPage">
        Introduction: Open Source vs. Product. Know Your Options
      </Box>
      <Box mb={4}>
        Open-source tools like{' '}
        <Box as="span" fontWeight="bold">
          Robyn
        </Box>{' '}
        (Meta) and{' '}
        <Box as="span" fontWeight="bold">
          Google Meridian
        </Box>{' '}
        offer powerful modeling flexibility, but they require engineering
        overhead. Merkatus.io offers a high-speed, AI-powered, productized
        alternative. Here&apos;s how they compare:
      </Box>
    </Box>
    <Divider my={12} />

    {/* Robyn Section */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        Robyn (Meta)
      </Box>
      <Box as="ul" mb={4} pl={6}>
        <Box as="li" mb={2}>
          <Box as="span" fontWeight="bold">
            Pros:
          </Box>{' '}
          Production-grade R-based MMM with adstock, saturation curves, ridge
          regression, and strong community support.
        </Box>
        <Box as="li" mb={2}>
          <Box as="span" fontWeight="bold">
            Cons:
          </Box>{' '}
          Needs R infrastructure, modeling expertise, ongoing maintenance.
        </Box>
      </Box>
    </Box>
    <Divider my={12} />

    {/* Google Meridian Section */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        Google Meridian
      </Box>
      <Box as="ul" mb={4} pl={6}>
        <Box as="li" mb={2}>
          <Box as="span" fontWeight="bold">
            Pros:
          </Box>{' '}
          Python-based Bayesian modeling library with customizable priors,
          transparent; great for experimentation.
        </Box>
        <Box as="li" mb={2}>
          <Box as="span" fontWeight="bold">
            Cons:
          </Box>{' '}
          Code-heavy so Python proficiency necessary, less enterprise-friendly,
          small user base.
        </Box>
      </Box>
    </Box>
    <Divider my={12} />

    {/* Merkatus.io Section */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        Merkatus.io (Productized MMM)
      </Box>
      <Box as="ul" mb={4} pl={6}>
        <Box as="li" mb={2}>
          <Box as="span" fontWeight="bold">
            Pros:
          </Box>{' '}
          AI-powered modeling, warehouse-native (GA4 → direct integration),
          no-code setup, fast time-to-insight, built-in normalization,
          integrates with dashboards you already use.
        </Box>
        <Box as="li" mb={2}>
          <Box as="span" fontWeight="bold">
            Cons:
          </Box>{' '}
          Less control over modeling parameters than OSS frameworks (but speed
          and ease often outweigh this).
        </Box>
      </Box>
    </Box>
    <Divider my={12} />

    {/* Decision Matrix */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        Decision Matrix
      </Box>
      <Box
        as="table"
        width="100%"
        mb={8}
        sx={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          border: '1px solid',
          borderColor: 'gray.200',
        }}>
        <Box as="thead" bg="gray.50">
          <Box as="tr">
            <Box
              as="th"
              p={4}
              textAlign="left"
              borderBottom="1px solid"
              borderColor="gray.200"
              fontWeight="bold">
              Criteria
            </Box>
            <Box
              as="th"
              p={4}
              textAlign="left"
              borderBottom="1px solid"
              borderColor="gray.200"
              fontWeight="bold">
              Robyn
            </Box>
            <Box
              as="th"
              p={4}
              textAlign="left"
              borderBottom="1px solid"
              borderColor="gray.200"
              fontWeight="bold">
              Google Meridian
            </Box>
            <Box
              as="th"
              p={4}
              textAlign="left"
              borderBottom="1px solid"
              borderColor="gray.200"
              fontWeight="bold">
              Merkatus.io
            </Box>
          </Box>
        </Box>
        <Box as="tbody">
          <Box as="tr">
            <Box
              as="td"
              p={4}
              borderBottom="1px solid"
              borderColor="gray.200"
              fontWeight="bold">
              Setup Complexity
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              High
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Medium
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Low
            </Box>
          </Box>
          <Box as="tr" bg="gray.50">
            <Box
              as="td"
              p={4}
              borderBottom="1px solid"
              borderColor="gray.200"
              fontWeight="bold">
              Modeling Control
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              High
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              High
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Moderate (AI-optimized)
            </Box>
          </Box>
          <Box as="tr">
            <Box
              as="td"
              p={4}
              borderBottom="1px solid"
              borderColor="gray.200"
              fontWeight="bold">
              Refresh Frequency
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Manual
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Manual
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Automated (weekly/daily)
            </Box>
          </Box>
          <Box as="tr" bg="gray.50">
            <Box
              as="td"
              p={4}
              fontWeight="bold"
              borderBottom="1px solid"
              borderColor="gray.200">
              Integration
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Custom
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Custom
            </Box>
            <Box as="td" p={4} borderBottom="1px solid" borderColor="gray.200">
              Native GA4, Ad platforms, dashboards
            </Box>
          </Box>
          <Box as="tr">
            <Box as="td" p={4} fontWeight="bold">
              Speed to Insights
            </Box>
            <Box as="td" p={4}>
              Days/weeks
            </Box>
            <Box as="td" p={4}>
              Hours/days
            </Box>
            <Box as="td" p={4}>
              Minutes/hours
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>

    <Divider my={12} />

    {/* Summary Section */}
    <Box mb={4}>
      <Box
        as="h2"
        fontSize="24px"
        fontWeight="bold"
        color="text.darkBlue.900"
        mb={4}
        mt={8}
        fontFamily="marketingPage">
        Summary: The Right MMM Depends on Your Team&apos;s Goals
      </Box>
      <Box mb={4}>
        Choosing the right MMM framework isn&apos;t just about technical
        capability - it&apos;s about speed, scalability, and how much hands-on
        modeling your team can sustain.
      </Box>
      <Box mb={4}>
        Open-source solutions like{' '}
        <Box as="span" fontWeight="bold">
          Robyn (Meta)
        </Box>{' '}
        and{' '}
        <Box as="span" fontWeight="bold">
          Google Meridian
        </Box>{' '}
        are strong choices for data science-driven organizations that want full
        transparency and control. They offer flexibility and deep customization,
        but at the cost of time, maintenance, and internal resources.
      </Box>
      <Box mb={4}>
        <Box as="span" fontWeight="bold">
          Merkatus.io
        </Box>{' '}
        takes a different approach. It delivers AI-powered, automated modeling
        that removes the data engineering barrier altogether. From GA4, CRM and
        ad platform integrations to automated normalization and validation, your
        team gets a production-grade MMM setup that&apos;s ready to run in
        hours, not weeks, without sacrificing analytical rigor (all the heavy
        lifting happens under the hood).
      </Box>
      <Box mb={4}>
        Whether you&apos;re comparing flexibility or efficiency, the trade-off
        is clear:
      </Box>
      <Box as="ul" mb={4} pl={6}>
        <Box as="li" mb={2}>
          <Box as="span" fontWeight="bold">
            Open-source
          </Box>{' '}
          frameworks require technical investment and modeling expertise.
        </Box>
        <Box as="li" mb={2}>
          <Box as="span" fontWeight="bold">
            Merkatus.io
          </Box>{' '}
          is built for business leaders, marketing and analytics teams who need
          rapid insights, clean data, and actionable results without the
          overhead of maintaining code or infrastructure.
        </Box>
      </Box>
      <Box mb={4} fontWeight="bold" fontSize="18px">
        Accelerate your marketing effectiveness journey — start modeling
        clarity, not complexity.
      </Box>
    </Box>
    <Divider my={12} />

    {/* CTA Section */}
    <Box mt={8}>
      <Box mb={6} color="text.darkBlue.800" fontSize="18px">
        Learn more at{' '}
        <Link
          href="https://merkatus.io"
          color="icon.green"
          textDecoration="underline"
          _hover={{ textDecoration: 'none' }}
          isExternal>
          https://merkatus.io
        </Link>
      </Box>
    </Box>
  </Box>
);
